import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, BookOpen, Star, Clock, Users, ArrowRight } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import Header from './Header';
import Hero from './Hero';
import Footer from './Footer';
import CookieConsent from './CookieConsent';
import SearchFilters from './SearchFilters';
import { topicsAPI, bookmarksAPI, fallbackStorage } from '../utils/api';
import { toast } from 'sonner';

const Home = () => {
  const [topics, setTopics] = useState([]);
  const [filteredTopics, setFilteredTopics] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const savedMode = localStorage.getItem('darkMode');
    const darkMode = savedMode !== null ? savedMode === 'true' : prefersDark;
    setIsDarkMode(darkMode);
    updateDarkMode(darkMode);

    loadData();
  }, []);

  const updateDarkMode = (isDark) => {
    const htmlElement = document.documentElement;
    if (isDark) {
      htmlElement.classList.add('dark');
    } else {
      htmlElement.classList.remove('dark');
    }
  };

  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    updateDarkMode(newMode);
    localStorage.setItem('darkMode', newMode);
  };

  const loadData = async () => {
    try {
      // Load topics and bookmarks in parallel for better performance
      const [topicsData, bookmarksData] = await Promise.all([
        topicsAPI.getAll(),
        bookmarksAPI.get()
      ]);
      
      // Validate topics data
      if (Array.isArray(topicsData)) {
        setTopics(topicsData);
        setFilteredTopics(topicsData);
        
        // Backup to localStorage only after successful server fetch
        if (topicsData.length > 0) {
          fallbackStorage.setTopics(topicsData);
        }
      } else {
        setTopics([]);
        setFilteredTopics([]);
      }
      
      // Validate bookmarks data
      setBookmarks(Array.isArray(bookmarksData) ? bookmarksData : []);
      
      setLoading(false);
    } catch (error) {
      console.error('Server API Error:', error);
      
      // If server fails, start fresh - don't use old localStorage data
      setTopics([]);
      setFilteredTopics([]);
      setBookmarks([]);
      setLoading(false);
      
      // Show error toast
      toast.error('Unable to connect to server. Creating fresh session.', {
        duration: 3000
      });
    }
  };

  const handleFiltered = (filtered) => {
    // For home page, limit to 10 latest topics
    const latestFiltered = filtered
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 10);
    setFilteredTopics(latestFiltered);
  };

  const handleTopicClick = (topicId) => {
    navigate(`/topic/${topicId}`);
  };

  const toggleBookmark = (topicId) => {
    // Optimized: Instant UI update without waiting for API
    const isBookmarked = bookmarks.includes(topicId);
    const newBookmarks = isBookmarked
      ? bookmarks.filter(id => id !== topicId)
      : [...bookmarks, topicId];
    
    setBookmarks(newBookmarks);
    fallbackStorage.setBookmarks(newBookmarks);
    toast.success(isBookmarked ? 'Removed from bookmarks' : 'Added to bookmarks');
    
    // Sync with API in background (non-blocking)
    if (isBookmarked) {
      bookmarksAPI.remove(topicId).catch(() => {});
    } else {
      bookmarksAPI.add(topicId).catch(() => {});
    }
  };

  // Memoize computed values for performance
  const topicsCount = useMemo(() => topics.length, [topics]);
  const bookmarkedCount = useMemo(() => bookmarks.length, [bookmarks]);
  const latestTopics = useMemo(() => {
    if (!topics.length) return [];
    return topics
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 10);
  }, [topics]);

  if (loading) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-velvet' : 'bg-cream'} flex items-center justify-center`}>
        <div className="text-center animate-fadeInUp">
          <BookOpen className={`h-16 w-16 ${isDarkMode ? 'text-gold' : 'text-sapphire'} mx-auto mb-4 animate-softPulse`} />
          <p className={`text-xl ${isDarkMode ? 'text-cream-light' : 'text-sapphire'}`}>Loading premium content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDarkMode ? 'bg-velvet text-cream-light' : 'bg-cream text-sapphire'}`}>
      {/* Header */}
      <Header isDarkMode={isDarkMode} onToggleDarkMode={toggleDarkMode} />

      {/* Hero Section */}
      <Hero onExploreClick={() => document.getElementById('courses-section')?.scrollIntoView({ behavior: 'smooth' })} isDarkMode={isDarkMode} />

      {/* Featured Courses Section */}
      <main id="courses-section" className="max-w-7xl mx-auto px-6 py-20">
        <div className="mb-16 animate-fadeInUp">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div>
              <h2 className="font-serif text-4xl sm:text-5xl font-bold mb-4 text-sapphire dark:text-cream-light">
                Latest Courses
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl">
                Discover our newest premium courses. Visit <span className="font-semibold">Explore</span> to browse all {topics.length} available courses.
              </p>
            </div>
            <Button 
              onClick={() => navigate('/explore')}
              className="btn-primary text-white border-0 whitespace-nowrap h-12"
            >
              <span>Explore All</span>
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mb-12 animate-slideInUp">
          <SearchFilters topics={topics} onFiltered={handleFiltered} isDarkMode={isDarkMode} />
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16 animate-slideInLeft">
          <div className="neumorphic-card text-center">
            <Users className={`h-8 w-8 ${isDarkMode ? 'text-gold' : 'text-sapphire'} mx-auto mb-3`} />
            <p className="text-3xl font-bold text-gold mb-1">{topics.length}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Courses Available</p>
          </div>
          
          <div className="neumorphic-card text-center">
            <Star className={`h-8 w-8 ${isDarkMode ? 'text-gold' : 'text-sapphire'} mx-auto mb-3`} />
            <p className="text-3xl font-bold text-gold mb-1">{bookmarks.length}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Bookmarked</p>
          </div>
          
          <div className="neumorphic-card text-center">
            <Clock className={`h-8 w-8 ${isDarkMode ? 'text-gold' : 'text-sapphire'} mx-auto mb-3`} />
            <p className="text-3xl font-bold text-gold mb-1">24/7</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Access</p>
          </div>
        </div>

        {/* Topics Grid */}
        {filteredTopics.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTopics.map((topic, index) => (
              <Card 
                key={topic.id} 
                className={`topic-card neumorphic-card border-0 animate-fadeInUp ${isDarkMode ? 'dark' : ''}`}
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`topic-card-${topic.id}`}
              >
                <CardHeader className="p-0">
                  <div className="relative">
                    <img 
                      src={topic.image} 
                      alt={topic.title}
                      className="w-full h-48 object-cover rounded-t-2xl"
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleBookmark(topic.id);
                      }}
                      className={`absolute top-4 right-4 p-2 rounded-full transition-all duration-300 ${
                        bookmarks.includes(topic.id) 
                          ? 'bg-gold text-velvet' 
                          : `${isDarkMode ? 'bg-sapphire-light text-cream-light hover:bg-gold hover:text-velvet' : 'bg-white/90 text-sapphire hover:bg-gold hover:text-velvet'}`
                      }`}
                      data-testid={`bookmark-btn-${topic.id}`}
                    >
                      <Star className="h-4 w-4" fill={bookmarks.includes(topic.id) ? 'currentColor' : 'none'} />
                    </button>
                  </div>
                </CardHeader>
                
                <CardContent className="p-6" onClick={() => handleTopicClick(topic.id)}>
                  <CardTitle className={`text-xl mb-3 font-serif ${isDarkMode ? 'text-cream-light' : 'text-sapphire'}`}>
                    {topic.title}
                  </CardTitle>
                  <CardDescription className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-4 line-clamp-2`}>
                    {topic.description}
                  </CardDescription>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {(topic.keyConcepts || []).slice(0, 3).map((concept, idx) => (
                      <Badge key={idx} variant="secondary" className={`${isDarkMode ? 'bg-gold/20 text-gold' : 'bg-gold/10 text-sapphire'}`}>
                        {concept}
                      </Badge>
                    ))}
                  </div>
                  
                  <div className="flex justify-between items-center gap-2">
                    <Button 
                      size="sm" 
                      className="flex-1 btn-primary text-white border-0 text-xs"
                      data-testid={`start-learning-btn-${topic.id}`}
                    >
                      <span>Learn</span>
                      <ArrowRight className="h-3 w-3 ml-1" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/quiz/${topic.id}`);
                      }}
                      className={`flex-1 ${isDarkMode ? 'border-gold text-gold hover:bg-gold hover:text-velvet' : 'border-sapphire text-sapphire hover:bg-sapphire hover:text-cream'} text-xs`}
                      data-testid={`take-quiz-btn-${topic.id}`}
                    >
                      Quiz
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-24 animate-fadeInUp">
            <BookOpen className={`h-16 w-16 ${isDarkMode ? 'text-gold/30' : 'text-sapphire/30'} mx-auto mb-4`} />
            <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-cream-light' : 'text-sapphire'} mb-2`}>No courses found</h3>
            <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Try adjusting your search terms</p>
          </div>
        )}
      </main>

      {/* Newsletter Section */}
      <section className={`my-20 px-6 py-16 rounded-2xl neumorphic-card mx-6 md:mx-auto max-w-2xl`}>
        <div className="text-center mb-8 animate-fadeInUp">
          <h3 className="font-serif text-3xl font-bold mb-3 text-sapphire dark:text-cream-light">
            Stay Updated
          </h3>
          <p className="text-gray-600 dark:text-gray-300">
            Subscribe to our newsletter for exclusive content and learning tips.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 animate-slideInUp">
          <input
            type="email"
            placeholder="Enter your email"
            className={`flex-1 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold transition-all ${
              isDarkMode
                ? 'bg-sapphire-light/30 border border-gold/30 text-cream-light placeholder-cream-light/50'
                : 'bg-white border border-silver text-sapphire placeholder-gray-400'
            }`}
          />
          <Button className="btn-gold font-semibold whitespace-nowrap">
            Subscribe
          </Button>
        </div>
      </section>

      {/* Footer */}
      <Footer isDarkMode={isDarkMode} />

      {/* Cookie Consent */}
      <CookieConsent isDarkMode={isDarkMode} />
    </div>
  );
};

export default Home;